import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { TextInput, ActionIcon } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import classes from './SearchBar.module.css';

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  ariaLabel?: string;
}

/**
 * Reusable SearchBar component
 * - Left search icon always visible
 * - Right clear icon appears when value has content
 * - Escape clears and refocuses input
 */
export const SearchBar = forwardRef<HTMLInputElement, SearchBarProps>(function SearchBar(
  {
    value,
    onChange,
    onClear,
    placeholder = 'Search',
    disabled = false,
    autoFocus = false,
    ariaLabel = 'Search',
  },
  ref
) {
  const localRef = useRef<HTMLInputElement | null>(null);
  const inputRef = (ref as React.MutableRefObject<HTMLInputElement | null>) || localRef;

  const hasValue = useMemo(() => value.trim().length > 0, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange(e.target.value);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    if (disabled) return;
    onClear();
    // Return focus to input after clearing
    setTimeout(() => {
      inputRef?.current?.focus();
    }, 0);
  }, [onClear, inputRef, disabled]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape' && hasValue && !disabled) {
        e.preventDefault();
        onClear();
        setTimeout(() => inputRef?.current?.focus(), 0);
      }
    },
    [hasValue, onClear, inputRef, disabled]
  );

  useEffect(() => {
    if (autoFocus) {
      inputRef?.current?.focus();
    }
  }, [autoFocus, inputRef]);

  return (
    <TextInput
      ref={inputRef}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      aria-label={ariaLabel}
      disabled={disabled}
      leftSection={<IconSearch size={24} aria-hidden className={classes.leftIcon} />}
      leftSectionWidth={36}
      rightSection={
        hasValue ? (
          <ActionIcon
            variant="subtle"
            onClick={handleClear}
            aria-label="Clear search"
            disabled={disabled}
            className={`${classes.rightIcon} ${disabled ? classes.clearButtonDisabled : classes.clearButton}`}
          >
            <IconX size={18} />
          </ActionIcon>
        ) : null
      }
      className={classes.root}
      classNames={{ input: `${classes.input} ${classes.placeholder}` }}
      size="md"
    />
  );
});
